import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { StripeAccountErrorItemType } from "@modules/finances/components/stripeAccountErrorAlert"
import { PaymentError, StripeErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { getStripeAccountErrorText } from "@modules/finances/utils/financeUtils"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import { ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"

export const useStripeAccountErrors = () => {
  const { userFinanceApi } = useServicesContext()

  const { data: stripeAccountErrors, isLoading } = useQuery({
    queryKey: ["stripeAccountErrors"],
    queryFn: () => userFinanceApi.getStripeAccountErrors(),
    staleTime: 0,
  })

  const { navigateToProgramView } = useProgramSidebar()
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()
  const queryClient = useQueryClient()

  useEffect(() => {
    // Set staleTime to 5min if there's no error
    if (
      !isLoading &&
      (!stripeAccountErrors ||
        (!stripeAccountErrors.accountError &&
          (!stripeAccountErrors.paymentErrors || stripeAccountErrors.paymentErrors.length === 0)))
    ) {
      queryClient.setQueryDefaults(["stripeAccountErrors"], { staleTime: 10 * 60 * 1000 }) // 10 min
    }
  }, [stripeAccountErrors, isLoading])

  const openProgramDetails = (programId: string) => {
    // Payment errors can only occur on subscription, so only for infinite programs
    navigateToProgramView(programId, ProgramInfiniteTypeOf, false)
  }

  const openProgramBundleDetails = (programBundleId: string) => {
    navigateToProgramBundleResume(programBundleId, false)
  }

  const formattedStripeAccountErrors = useMemo(() => {
    const errors: StripeAccountErrorItemType[] = []

    if (stripeAccountErrors?.accountError) {
      errors.push({
        ...getStripeAccountErrorText(stripeAccountErrors.accountError),
        openStripeAccountOnButtonClick: true,
      })
    }

    stripeAccountErrors?.paymentErrors.forEach((paymentError) => {
      errors.push({
        alertTitle: word("global.stripeError.paymentProblem.title"),
        alertSubtitle: getPaymentErrorSubtitle(paymentError),
        alertContent: getPaymentErrorContent(paymentError),
        primaryButtonLabel:
          paymentError.programId || paymentError.programBundleId
            ? word("global.stripeError.paymentProblem.program.buttonLabel")
            : undefined,
        primaryButtonOnClickFn: () => {
          if (paymentError.programId) openProgramDetails(paymentError.programId)
          if (paymentError.programBundleId) openProgramBundleDetails(paymentError.programBundleId)
        },
      })
    })

    return errors
  }, [stripeAccountErrors])

  return formattedStripeAccountErrors
}

export function getPaymentErrorSubtitle(paymentError: PaymentError): string {
  switch (paymentError.errorCode) {
    case StripeErrorCode.CARD_DECLINED:
    case StripeErrorCode.DO_NOT_HONOR:
      return word(`global.stripeError.paymentProblem.CARD_DECLINED.label`)
    case StripeErrorCode.EXPIRED_CARD:
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return word(`global.stripeError.paymentProblem.${paymentError.errorCode}.label`, {
        subscriptionName: paymentError.subscriptionName,
        cardNumber: paymentError.last4Digits,
      })
    case StripeErrorCode.PAYMENT_INTENT_AUTHENTICATION_FAILURE:
      return word(`global.stripeError.paymentProblem.PAYMENT_INTENT_AUTHENTICATION_FAILURE.label`)
    default:
      return word(`global.stripeError.paymentProblem.fallback.label`, {
        subscriptionName: paymentError.subscriptionName,
        cardNumber: paymentError.last4Digits,
      })
  }
}

export function getPaymentErrorContent(paymentError: PaymentError): string {
  switch (paymentError.errorCode) {
    case StripeErrorCode.DO_NOT_HONOR:
    case StripeErrorCode.CARD_DECLINED:
      if (paymentError.last4Digits) {
        return word(`global.stripeError.paymentProblem.CARD_DECLINED.content`, {
          subscriptionName: paymentError.subscriptionName,
          cardNumber: paymentError.last4Digits,
        })
      } else {
        return word(`global.stripeError.paymentProblem.fallback.contentNoCardNumber`, {
          subscriptionName: paymentError.subscriptionName,
        })
      }
    case StripeErrorCode.PAYMENT_INTENT_AUTHENTICATION_FAILURE:
      return word(`global.stripeError.paymentProblem.PAYMENT_INTENT_AUTHENTICATION_FAILURE.content`, {
        subscriptionName: paymentError.subscriptionName,
      })
    case StripeErrorCode.EXPIRED_CARD:
    case StripeErrorCode.AUTHENTICATION_REQUIRED:
      return word(`global.stripeError.paymentProblem.${paymentError.errorCode}.content`, {
        subscriptionName: paymentError.subscriptionName,
        cardNumber: paymentError.last4Digits,
      })
    default:
      if (paymentError.last4Digits) {
        return word(`global.stripeError.paymentProblem.fallback.content`, {
          subscriptionName: paymentError.subscriptionName,
          cardNumber: paymentError.last4Digits,
        })
      } else {
        return word(`global.stripeError.paymentProblem.fallback.contentNoCardNumber`, {
          subscriptionName: paymentError.subscriptionName,
        })
      }
  }
}

export function getPaymentErrorButtonLabel(paymentError: PaymentError): string {
  return paymentError.errorCode === StripeErrorCode.AUTHENTICATION_REQUIRED
    ? word("global.stripeError.paymentProblem.program.authenticationRequired")
    : word("global.stripeError.paymentProblem.program.cardSettings")
}
